/**
 * WeeklyInsightsCard — Feb 2026
 *
 * Reads `/api/insights/weekly` and renders a compact week-over-week
 * summary of the two behavioral metrics we already coach on:
 *   - Reply rate (openers received → replied to)
 *   - Right-swipe rate
 *
 * The card intentionally shows PROGRESS, not just current values, so
 * users feel the coaching working. A `null` metric (no data yet in
 * the window) is skipped instead of rendered as "0%". If neither
 * metric has data, the whole card hides.
 *
 * Tap → /discover (there's nothing more actionable on this card;
 * the numbers themselves are the payoff).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, Sparkles } from 'lucide-react';
import { api } from '@/contexts/AuthContext';

function deltaAccent(delta) {
  if (delta === null || delta === undefined) return { color: '#8A8A93', Icon: Minus };
  if (delta > 3)  return { color: '#15803D', Icon: TrendingUp };
  if (delta < -3) return { color: '#B91C1C', Icon: TrendingDown };
  return { color: '#8A8A93', Icon: Minus };
}

function formatDelta(delta) {
  if (delta === null || delta === undefined) return '—';
  const s = delta > 0 ? '+' : '';
  return `${s}${delta} pts`;
}

export default function WeeklyInsightsCard() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancel = false;
    api.get('/insights/weekly').then((r) => {
      if (cancel) return;
      setMetrics(r.data?.metrics || []);
      setLoaded(true);
    }).catch(() => { if (!cancel) setLoaded(true); });
    return () => { cancel = true; };
  }, []);

  if (!loaded || metrics.length === 0) return null;

  return (
    <button
      onClick={() => navigate('/discover')}
      data-testid="weekly-insights-card"
      className="mt-3 w-full rounded-2xl bg-white p-4 text-left transition-shadow hover:shadow-[0_4px_14px_rgba(15,15,16,0.06)]"
      style={{ border: '1px solid rgba(15,15,16,0.06)' }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.14), rgba(30,79,216,0.14))' }}
        >
          <Sparkles size={13} className="text-[#7C3AED]"/>
        </span>
        <span className="text-[10px] font-[900] uppercase tracking-[0.16em] text-[#7C3AED]">
          Dating Insights · This week
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {metrics.map((m) => {
          const { color, Icon } = deltaAccent(m.delta_pts);
          return (
            <div key={m.key} data-testid={`weekly-metric-${m.key}`}>
              <p className="text-[10.5px] font-[700] uppercase tracking-[0.12em] text-[#8A8A93]">{m.label}</p>
              <p className="mt-1 text-[22px] font-[800] tabular-nums leading-none tracking-[-0.6px] text-[#0F0F10]">
                {m.this_week}<span className="text-[#8A8A93] text-[13px] font-[600]">%</span>
              </p>
              <div className="mt-2 flex items-center gap-1.5 text-[11.5px] font-[700]" style={{ color }}>
                <Icon size={12} />
                <span>{formatDelta(m.delta_pts)}</span>
                <span className="text-[#8A8A93] font-[500]">vs last week</span>
              </div>
              <p className="mt-1.5 text-[11.5px] text-[#6B7280] leading-snug">{m.headline}</p>
            </div>
          );
        })}
      </div>
    </button>
  );
}
